export interface LeaveBalance {
  id: string;
  leave_type_id: string;
  total_days: number;
  used_days: number;
  accrued_days: number;
  leave_types: {
    name: string;
    description: string;
    annual_days: number;
    monthly_accrual_rate: number;
  };
}

export interface LeaveRequest {
  id: string;
  employee_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  medical_certificate_url?: string;
}

export interface LeaveType {
  id: string;
  name: string;
  description: string;
  annual_days: number;
  monthly_accrual_rate: number;
}

export async function getLeaveBalances(employeeId: string): Promise<LeaveBalance[]> {
  const response = await fetch(`/api/leave/balance`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ employeeId }),
  });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch leave balances');
  }
  return response.json();
}

export async function getLeaveTypes(): Promise<LeaveType[]> {
  const response = await fetch('/api/leave/types', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to fetch leave types');
  }

  return response.json();
}

export async function submitLeaveRequest(data: {
  employeeId: string;
  leaveTypeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  medicalCertificate?: File;
  isHalfDay?: boolean;
  halfDayType?: 'AM' | 'PM';
}): Promise<LeaveRequest> {
  let medicalCertificateUrl: string | undefined;

  if (data.medicalCertificate) {
    // Upload medical certificate using Images API
    const formData = new FormData();
    formData.append('image', data.medicalCertificate);

    const uploadResponse = await fetch('/api/images/upload', {
      method: 'POST',
      body: formData,
    });

    if (!uploadResponse.ok) {
      const error = await uploadResponse.json();
      throw new Error(error.error || 'Failed to upload medical certificate');
    }

    const uploadResult = await uploadResponse.json();
    medicalCertificateUrl = uploadResult.url;
  }

  const response = await fetch('/api/leave/request', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      employeeId: data.employeeId,
      leaveTypeId: data.leaveTypeId,
      startDate: data.startDate,
      endDate: data.endDate,
      reason: data.reason,
      medicalCertificateUrl,
      isHalfDay: data.isHalfDay || false,
      halfDayType: data.isHalfDay ? data.halfDayType : undefined,
    }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to submit leave request');
  }

  return response.json();
}

export async function approveLeaveRequest(data: {
  requestId: string;
  status: 'APPROVED' | 'REJECTED';
  adminId: string;
}): Promise<void> {
  const response = await fetch('/api/leave/approve', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || 'Failed to approve leave request');
  }
} 